/**
 * UI component for feedback form on account screen.
 * Uses Redux for state management and Firebase for storing feedback.
 * Handles form state, validation, submission, and displays success/error messages.
 * Has fields for email, name, and message.
 */

//react and redux hooks
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";

//redux actions
import { submitFeedback, resetFeedbackState } from "../../features/feedback/feedbackSlice";

//languages
import { useTranslation } from "react-i18next";

//styles
import { FeedbackFormDiv, FeedbackFormInput, Form, Button, FeedbackFormTextarea, FeedbackFormPanel, FeedbackFormSubtitle } from "../../styles/styles";

//utils
import { getAppVersion } from "../../utils/getAppVersion";


import { DEFAULT_FEEDBACK_SUBJECT } from "../../utils/feedbackSubjects";
import FeedbackSubjectSelect from "../shared/feedbackSubjectSelect/feedbackSubjectSelect";

const FeedbackForm = ({
  showSubtitle = false,
  withPanel = false,
  subtitleInsidePanel = false,
  routeOverride,
}) => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector(s => s.users); // у тебя уже есть
  const { sending, success, error } = useSelector(s => s.feedback || {});

  const { t } = useTranslation(['account']);

  // local form state
  const [email, setEmail] = useState(currentUser?.email || "");
  const [name, setName]   = useState(currentUser?.name || "");
  const [subject, setSubject] = useState(DEFAULT_FEEDBACK_SUBJECT);
  const [message, setMessage] = useState("");

  // simple validation - only email required
  const emailValid = /\S+@\S+\.\S+/.test((email||"").trim());
  const isValid = emailValid;


  // reset success message after 3 seconds
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => dispatch(resetFeedbackState()), 3000);
      return () => clearTimeout(t);
    }
  }, [success, dispatch]);

  // handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || sending) return;

    dispatch(submitFeedback({
      message,
      email,
      name,
      subject,
      subjectLabel: t(`account:form.subject.${subject}`),
      userId: currentUser?.id || null,
      plan: currentUser?.plan || "free",
      route: routeOverride || window.location.pathname,
      userAgent: navigator.userAgent,
      appVersion: getAppVersion()
    })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        setMessage("");        
      }
    });
  };

  const outlinedFields = withPanel;

  const formFields = (
    <>
      <FeedbackFormDiv>
        <FeedbackFormInput
          $outlined={outlinedFields}
          placeholder={t('account:form.placeholderEmail')}
          id="feedback-email"
          type="email"
          name="feedback-email"
          value={email}
          onChange={(e)=>setEmail(e.target.value)}
          required
          aria-required="true"
        />
      </FeedbackFormDiv>

      <FeedbackFormDiv>
        <FeedbackFormInput
          $outlined={outlinedFields}
          placeholder={t('account:form.placeholderName')}
          id="feedback-name"
          type="text"
          name="feedback-name"
          value={name}
          onChange={(e)=>setName(e.target.value)}
        />
      </FeedbackFormDiv>

      <FeedbackSubjectSelect value={subject} onChange={setSubject} outlined={outlinedFields} />

      <FeedbackFormDiv>
        <FeedbackFormTextarea
          $outlined={outlinedFields}
          as="textarea"
          rows={6}
          id="feedback-message"
          name="feedback-message"
          placeholder={t('account:form.placeholderMessage')}
          value={message}
          onChange={(e)=>setMessage(e.target.value)}
        />
      </FeedbackFormDiv>

      <Button 
        greenButton 
        type="submit" 
        width="auto" 
        disabled={!isValid || sending}
      >
        {sending ? t('account:form.sending') : t('account:form.send')}
      </Button>

      {success && (
        <div role="status" style={{ marginTop: 8, color: "#2e7d32" }}>
          {t('account:form.submissionMessage')}
        </div>
      )}
      {error && (
        <div id="feedback-error" role="alert" style={{ marginTop: 8, color: "#c62828" }}>
          {error}
        </div>
      )}
    </>
  );

  const subtitle = showSubtitle ? (
    <FeedbackFormSubtitle>{t('account:feedback.subtitle')}</FeedbackFormSubtitle>
  ) : null;

  const panelContent = (
    <>
      {subtitleInsidePanel && subtitle}
      {formFields}
    </>
  );

  return (
    <Form align="flex-start" onSubmit={handleSubmit} aria-label="Feedback form" gap={showSubtitle && !subtitleInsidePanel ? "12px" : "15px"}>
      {showSubtitle && !subtitleInsidePanel && subtitle}

      {withPanel ? <FeedbackFormPanel>{panelContent}</FeedbackFormPanel> : formFields}
    </Form>
  );
};

export default FeedbackForm;
